<template>
  <div>
    <!-- Regression banner -->
    <v-alert v-if="bannerCount > 0" type="error" dense text border="left" class="mb-3">
      <div class="d-flex align-center">
        <span class="font-weight-medium">
          {{ bannerCount }} recurring issue(s) detected in the last 24 hours.
        </span>
        <v-spacer></v-spacer>
        <v-btn small outlined color="error" class="text-none" @click="subTab = 2">View</v-btn>
      </div>
    </v-alert>

    <!-- Sub-tabs -->
    <v-tabs v-model="subTab" height="38px" class="mb-2">
      <v-tab class="text-none">Summary</v-tab>
      <v-tab class="text-none">Insights</v-tab>
      <v-tab class="text-none">
        By date
        <v-chip v-if="navCounts.recent" x-small class="ml-2 count-badge" color="primary" dark>{{ navCounts.recent }}</v-chip>
      </v-tab>
      <v-tab class="text-none">
        Unmatched
        <v-chip v-if="navCounts.unmatched" x-small class="ml-2 count-badge" color="primary" dark>{{ navCounts.unmatched }}</v-chip>
      </v-tab>
      <v-tab class="text-none">
        Solved Reasons
        <v-icon v-if="!canAccessSolved" small class="ml-1" color="grey">mdi-lock</v-icon>
        <v-chip v-if="canAccessSolved && navCounts.solved" x-small class="ml-2 count-badge" color="primary" dark>{{ navCounts.solved }}</v-chip>
      </v-tab>
    </v-tabs>
    <v-divider class="mb-3"></v-divider>

    <v-tabs-items v-model="subTab">
      <!-- ============== SUMMARY ============== -->
      <v-tab-item>
        <div v-if="!drillStage">
          <v-row dense>
            <v-col cols="12" sm="6" md="3" v-for="(c, i) in summaryCards" :key="i">
              <div class="stat-card" :class="c.tone">
                <div class="stat-icon" :class="c.tone">
                  <v-icon :color="c.iconColor" size="20">{{ c.icon }}</v-icon>
                </div>
                <div class="stat-line">
                  <span class="stat-label">{{ c.label1 }}</span>
                  <span class="stat-value" :class="c.valueClass">{{ c.value1 }}</span>
                </div>
                <div class="stat-line stat-line-2">
                  <span class="stat-label">{{ c.label2 }}</span>
                  <span class="stat-value stat-value-sm">{{ c.value2 }}</span>
                </div>
              </div>
            </v-col>
          </v-row>

          <div class="d-flex align-center mt-4 mb-2">
            <div class="text-subtitle-1 font-weight-bold">By stage</div>
            <span class="grey--text caption ml-2">· {{ windowLabel }}</span>
            <v-spacer></v-spacer>
            <v-select
              v-model="windowSel" :items="windowOptions" item-text="label" item-value="key"
              label="Time range" dense outlined hide-details class="compact-select" style="max-width: 170px"
              @change="loadSummary"
            ></v-select>
          </div>

          <v-data-table
            :headers="summaryHeaders" :items="summaryRows" :loading="summaryLoading"
            dense item-key="stage" hide-default-footer :items-per-page="-1"
            class="elevation-0 rounded-lg" :no-data-text="'No data in this window.'"
          >
            <template v-slot:[`item.stage`]="{ item }">
              <span class="primary--text font-weight-bold">{{ item.stage }}</span>
            </template>
            <template v-slot:[`item.regressing_count`]="{ item }">
              <span v-if="item.regressing_count > 0" class="error--text font-weight-medium">
                {{ item.regressing_count }}
              </span>
              <span v-else class="grey--text">0</span>
            </template>
            <template v-slot:[`item.regressions_win`]="{ item }">
              <span v-if="item.regressions_win > 0" class="error--text">{{ item.regressions_win }}</span>
              <span v-else class="grey--text">0</span>
            </template>
            <template v-slot:[`item.unmatched_win`]="{ item }">
              <span v-if="item.unmatched_win > 0" class="warning--text">{{ item.unmatched_win }}</span>
              <span v-else class="grey--text">0</span>
            </template>
            <template v-slot:[`item.action`]="{ item }">
              <v-btn small color="primary" class="text-none elevation-0" @click="openStage(item.stage)">View</v-btn>
            </template>
          </v-data-table>
        </div>

        <!-- Stage drill-down -->
        <div v-else>
          <div class="d-flex align-center mb-2">
            <v-btn text small class="text-none" color="primary" @click="drillStage = ''">
              <v-icon left small>mdi-arrow-left</v-icon> Back to summary
            </v-btn>
            <v-spacer></v-spacer>
            <v-select
              v-model="windowSel" :items="windowOptions" item-text="label" item-value="key"
              label="Time range" dense outlined hide-details class="compact-select" style="max-width: 170px"
              @change="loadStage"
            ></v-select>
          </div>
          <div class="text-subtitle-1 mb-3">
            <span class="primary--text font-weight-bold">{{ drillStage }}</span>
            <span class="grey--text"> · per-reason breakdown</span>
          </div>
          <v-data-table
            :headers="stageHeaders" :items="stageRows" :loading="stageLoading"
            dense item-key="reason" :items-per-page="50"
            :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
            class="elevation-0 rounded-lg" no-data-text="No reasons recorded."
          >
            <template v-slot:[`item.status`]="{ item }">
              <span class="status-cell">
                <span class="status-dot" :class="statusDotClass(item)"></span>
                <span :class="statusTextClass(item)" class="font-weight-medium">{{ statusLabel(item) }}</span>
              </span>
            </template>
            <template v-slot:[`item.recurred_in_window`]="{ item }">
              <template v-if="item.status === 'Solved'">
                <span v-if="item.recurred_in_window > 0" class="error--text font-weight-medium">
                  {{ item.recurred_in_window }}
                  <span v-if="item.recurred_all_time > item.recurred_in_window" class="grey--text">
                    / {{ item.recurred_all_time }} all-time
                  </span>
                </span>
                <span v-else class="success--text">0</span>
              </template>
              <span v-else class="grey--text">—</span>
            </template>
          </v-data-table>
        </div>
      </v-tab-item>

      <!-- ============== INSIGHTS ============== -->
      <v-tab-item>
        <div class="d-flex align-center mb-3" style="gap: 10px;">
          <div class="text-subtitle-1 font-weight-bold">Insights</div>
          <v-spacer></v-spacer>
          <v-select
            v-model="windowSel" :items="windowOptions" item-text="label" item-value="key"
            label="Time range" dense outlined hide-details class="compact-select" style="max-width: 170px"
            @change="loadAnalytics"
          ></v-select>
          <v-btn color="primary" class="text-none elevation-0" :loading="reportLoading"
            :disabled="reportLoading" @click="downloadReport">
            <v-icon left small>mdi-download</v-icon> Download report
          </v-btn>
        </div>

        <v-row dense>
          <v-col cols="12" sm="6" md="3" v-for="(c, i) in insightCards" :key="i">
            <div class="stat-card" :class="c.tone">
              <div class="stat-icon" :class="c.tone">
                <v-icon :color="c.iconColor" size="20">{{ c.icon }}</v-icon>
              </div>
              <template v-if="c.stacked">
                <div class="stat-label">{{ c.label1 }}</div>
                <div class="stat-value stat-value-text text-truncate" :class="c.valueClass" :title="String(c.value1)">{{ c.value1 }}</div>
                <div class="stat-line stat-line-2">
                  <span class="stat-label">{{ c.label2 }}</span>
                  <span class="stat-value stat-value-sm">{{ c.value2 }}</span>
                </div>
              </template>
              <template v-else>
                <div class="stat-line">
                  <span class="stat-label">{{ c.label1 }}</span>
                  <span class="stat-value" :class="c.valueClass">{{ c.value1 }}</span>
                </div>
                <div class="stat-line stat-line-2">
                  <span class="stat-label">{{ c.label2 }}</span>
                  <span class="stat-value stat-value-sm">{{ c.value2 }}</span>
                </div>
              </template>
            </div>
          </v-col>
        </v-row>

        <v-card outlined class="rounded-lg pa-4 mt-4">
          <div class="d-flex align-center mb-2">
            <div class="subtitle-2 font-weight-bold">Rejections over time</div>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="trendMode" dense mandatory @change="renderTrend">
              <v-btn small value="day" class="text-none">Daily</v-btn>
              <v-btn small value="month" class="text-none">Monthly</v-btn>
            </v-btn-toggle>
          </div>
          <div v-if="!hasTrendData" class="text-center grey--text py-8">No rejections in this window.</div>
          <div v-else style="position: relative; height: 280px;">
            <canvas ref="trendChart"></canvas>
          </div>
        </v-card>

        <v-row dense class="mt-2">
          <v-col cols="12" md="6">
            <v-card outlined class="rounded-lg pa-4">
              <div class="subtitle-2 font-weight-bold mb-2">Top 10 reasons · {{ windowLabel }}</div>
              <div v-if="!(analytics.top_reasons && analytics.top_reasons.length)" class="text-center grey--text py-8">No rejections.</div>
              <div v-else style="position: relative; height: 360px;">
                <canvas ref="topReasonsChart"></canvas>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined class="rounded-lg pa-4">
              <div class="subtitle-2 font-weight-bold mb-2">Worst recurring · {{ windowLabel }}</div>
              <div v-if="!(analytics.top_recurring && analytics.top_recurring.length)" class="text-center grey--text py-8">No recurring rejections. ✨</div>
              <div v-else style="position: relative; height: 360px;">
                <canvas ref="topRecurringChart"></canvas>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-card outlined class="rounded-lg pa-4 mt-2">
          <div class="subtitle-2 font-weight-bold mb-2">
            Successful fixes — solved &amp; never recurred ({{ (analytics.successful_fixes || []).length }})
          </div>
          <div v-if="!(analytics.successful_fixes && analytics.successful_fixes.length)" class="text-center grey--text py-4">
            No solved reasons yet.
          </div>
          <v-row v-else dense>
            <v-col cols="12" sm="6" md="4" v-for="(w, i) in analytics.successful_fixes" :key="i">
              <v-card outlined color="green lighten-5" class="pa-3">
                <div class="caption success--text font-weight-bold">{{ w.stage }}</div>
                <div class="body-2">{{ w.normalized_reason }}</div>
                <div class="caption grey--text">since {{ fmtDate(w.solved_date) }}</div>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-tab-item>

      <!-- ============== BY DATE ============== -->
      <v-tab-item>
        <v-card outlined class="rounded-lg pa-4 mb-3">
          <v-row dense align="end">
            <v-col cols="12" sm="3" md="2">
              <v-select v-model="quickWindow" :items="quickWindowItems" item-text="label" item-value="key"
                label="Quick range" dense outlined hide-details @change="onQuickWindow"></v-select>
            </v-col>
            <v-col cols="12" sm="3" md="2">
              <v-text-field v-model="bdStart" type="date" label="Start" dense outlined hide-details></v-text-field>
            </v-col>
            <v-col cols="12" sm="3" md="2">
              <v-text-field v-model="bdEnd" type="date" label="End" dense outlined hide-details></v-text-field>
            </v-col>
            <v-col cols="12" sm="3" md="2">
              <v-select v-model="bdStage" :items="stageItems" label="Stage" dense outlined hide-details></v-select>
            </v-col>
            <v-col cols="auto">
              <v-btn color="primary" class="text-none elevation-0" @click="loadByDate">Load</v-btn>
            </v-col>
            <v-col cols="auto">
              <v-btn color="success" class="text-none elevation-0" :href="byDateExportUrl" target="_blank">
                <v-icon left small>mdi-download</v-icon> Export Excel
              </v-btn>
            </v-col>
          </v-row>
          <div class="caption grey--text mt-2">
            Showing <strong>{{ bdStart }}</strong> → <strong>{{ bdEnd }}</strong>
            <span v-if="bdStage !== 'ALL'"> · stage <strong>{{ bdStage }}</strong></span>
            · <strong>{{ bdRows.length }}</strong> rows
          </div>
        </v-card>

        <v-data-table
          :headers="byDateHeaders" :items="bdRows" :loading="bdLoading" dense
          :items-per-page="50" :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
          class="elevation-0 rounded-lg" no-data-text="No rows in this range."
        >
          <template v-slot:[`item.stage`]="{ item }">
            <span class="primary--text font-weight-bold">{{ item.stage }}</span>
          </template>
          <template v-slot:[`item.recorded_at`]="{ item }">
            <span class="text-no-wrap">{{ fmtDateTime(item.recorded_at) }}</span>
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <span class="status-cell">
              <span class="status-dot" :class="byDateDotClass(item.status)"></span>
              <span :class="byDateTextClass(item.status)" class="font-weight-medium">{{ item.status }}</span>
            </span>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- ============== UNMATCHED ============== -->
      <v-tab-item>
        <div class="text-subtitle-1 font-weight-bold mb-1">Unmatched / low-confidence reasons</div>
        <div class="caption grey--text mb-3" style="max-width: 760px;">
          Original reasons that the system could not confidently classify. Mapping one to a standard reason will also re-classify every past row that had the same original text.
        </div>
        <v-data-table
          :headers="unmatchedHeaders" :items="unmatchedRows" :loading="unmatchedLoading" dense
          :items-per-page="50" :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
          class="elevation-0 rounded-lg" no-data-text="Queue is empty."
        >
          <template v-slot:[`item.stage`]="{ item }">
            <span class="primary--text font-weight-bold">{{ item.stage }}</span>
          </template>
          <template v-slot:[`item.suggested`]="{ item }">
            <span v-if="item.suggested" class="grey--text font-italic">
              {{ item.suggested }} <small>({{ item.suggested_score }})</small>
            </span>
            <span v-else class="grey--text">—</span>
          </template>
          <template v-slot:[`item.action`]="{ item }">
            <div class="d-flex align-center" style="gap: 6px;">
              <v-select
                v-model="item._choice" :items="item.canonical_options"
                dense outlined hide-details class="map-select" style="max-width: 260px;"
              ></v-select>
              <v-btn small color="primary" class="text-none elevation-0 action-btn" @click="mapRow(item)">Map</v-btn>
            </div>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- ============== SOLVED ============== -->
      <v-tab-item>
        <div v-if="!canAccessSolved" class="text-center py-12">
          <v-icon size="56" color="grey lighten-1">mdi-lock-outline</v-icon>
          <div class="text-subtitle-1 font-weight-bold mt-3 grey--text text--darken-2">Restricted access</div>
          <div class="caption grey--text mt-1">You don't have permission to manage solved reasons.</div>
        </div>
        <template v-else>
        <div class="d-flex align-center mb-3">
          <div class="text-subtitle-1 font-weight-bold">Manage normalized reasons</div>
          <v-spacer></v-spacer>
          <v-select v-model="solvedStageFilter" :items="['ALL', ...stages]" label="Stage" dense outlined hide-details class="compact-select" style="max-width: 170px"></v-select>
        </div>

        <v-data-table
          :headers="solvedHeaders" :items="filteredSolvedRows" :loading="solvedLoading" dense
          :items-per-page="50" :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
          class="elevation-0 rounded-lg" no-data-text="No reasons in this filter."
        >
          <template v-slot:[`item.stage`]="{ item }">
            <span class="primary--text font-weight-bold">{{ item.stage }}</span>
          </template>
          <template v-slot:[`item.solved`]="{ item }">
            <span class="status-cell">
              <span class="status-dot" :class="item.solved ? 'dot-green' : 'dot-amber'"></span>
              <span :class="item.solved ? 'success--text' : 'warning--text'" class="font-weight-medium">
                {{ item.solved ? 'Solved' : 'Unsolved' }}
              </span>
            </span>
          </template>
          <template v-slot:[`item.solved_in`]="{ item }">
            <template v-if="item.solved">
              <span v-if="hasFE(item)" class="pill pill-fe mr-1">FE</span>
              <span v-if="hasBE(item)" class="pill pill-be mr-1">BE</span>
              <span v-if="hasVendor(item)" class="pill pill-vendor">Vendor</span>
            </template>
            <span v-else class="grey--text">—</span>
          </template>
          <template v-slot:[`item.solved_date`]="{ item }">
            <span v-if="item.solved">{{ item.solved_date }}</span>
            <span v-else class="grey--text">—</span>
          </template>
          <template v-slot:[`item.fix_description`]="{ item }">
            <span class="grey--text">{{ (item.solved && item.fix_description) || '—' }}</span>
          </template>
          <template v-slot:[`item.action`]="{ item }">
            <v-btn v-if="!item.solved" small color="primary" class="text-none elevation-0 action-btn" @click="openMarkSolved(item)">Mark solved</v-btn>
            <v-btn v-else small color="warning" outlined class="text-none action-btn" @click="askReopen(item)">Reopen</v-btn>
          </template>
        </v-data-table>

        <!-- Mark-solved dialog -->
        <v-dialog v-model="markDialog.open" max-width="520">
          <v-card>
            <v-card-title class="text-subtitle-1 font-weight-bold">
              Mark as Solved
            </v-card-title>
            <v-card-subtitle>{{ markDialog.stage }} · {{ markDialog.reason }}</v-card-subtitle>
            <v-card-text>
              <v-form @submit.prevent="submitMarkSolved">
                <v-row dense>
                  <v-col cols="6">
                    <v-select v-model="markDialog.solved_in" :items="solvedInItems" label="Solved In" dense outlined hide-details></v-select>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field v-model="markDialog.solved_date" type="date" label="Solved Date" dense outlined hide-details></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea v-model="markDialog.fix_description" label="Fix description (optional)" rows="3" dense outlined hide-details></v-textarea>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text class="text-none" @click="markDialog.open = false">Cancel</v-btn>
              <v-btn color="primary" class="text-none elevation-0" @click="submitMarkSolved">Mark as Solved</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Reopen confirm -->
        <v-dialog v-model="reopenDialog.open" max-width="420">
          <v-card>
            <v-card-title class="text-subtitle-1 font-weight-bold">Reopen this reason?</v-card-title>
            <v-card-text>
              "{{ reopenDialog.reason }}" will move back to Unsolved. Recurrence tracking for it will stop.
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text class="text-none" @click="reopenDialog.open = false">Cancel</v-btn>
              <v-btn color="warning" class="text-none elevation-0" @click="confirmReopen">Reopen</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        </template>
      </v-tab-item>
    </v-tabs-items>

  </div>
</template>

<script>
import axios from 'axios';
import { Chart, registerables } from 'chart.js';
import { apirej } from '../api';

Chart.register(...registerables);

const REJ_BASE = apirej + '/rejections';
const rejHttp = axios.create({ baseURL: REJ_BASE });
const rejectionApi = {
  meta() { return rejHttp.get('/meta').then(r => r.data); },
  analytics(w) { return rejHttp.get('/analytics', { params: { window: w } }).then(r => r.data); },
  summary(w) { return rejHttp.get('/summary', { params: { window: w } }).then(r => r.data); },
  perStage(stage, w) { return rejHttp.get('/stage/' + stage, { params: { window: w } }).then(r => r.data); },
  byDate(p) { return rejHttp.get('/by-date', { params: p }).then(r => r.data); },
  byDateExportUrl(p) {
    const qs = new URLSearchParams({ start: p.start || '', end: p.end || '', stage: p.stage || 'ALL' }).toString();
    return REJ_BASE + '/by-date/export?' + qs;
  },
  unmatched() { return rejHttp.get('/unmatched').then(r => r.data); },
  mapUnmatched(b) { return rejHttp.post('/unmatched/map', b).then(r => r.data); },
  solvedList() { return rejHttp.get('/solved').then(r => r.data); },
  solvedAdd(b) { return rejHttp.post('/solved', b).then(r => r.data); },
  solvedDelete(id) { return rejHttp.delete('/solved/' + id).then(r => r.data); },
  weeklyReport(days) {
    return rejHttp.get('/weekly-report', { params: { days }, responseType: 'blob' });
  },
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a);
  a.click(); a.remove(); URL.revokeObjectURL(url);
}

function parseFilenameFromCD(cd) {
  if (!cd) return null;
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  return m ? decodeURIComponent(m[1]) : null;
}

const WINDOW_TO_DAYS = { '7d': 7, '30d': 30, '90d': 90, '6m': 180, '1y': 365, '2y': 730, '5y': 1825, 'all': 3650 };

export default {
  name: 'RejectionAnalysis',
  props: {
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      booted: false,
      uuid: null,
      subTab: 0,
      windowSel: '30d',
      windowOptions: [],
      stages: [],
      stageCanonical: {},
      windowLabel: '',

      // weekly report download
      reportLoading: false,

      // summary
      summaryLoading: false,
      summaryRows: [],
      kpis: {},
      bannerCount: 0,
      navCounts: { recent: 0, unmatched: 0, solved: 0 },

      // stage drill-down
      drillStage: '',
      stageLoading: false,
      stageRows: [],

      // insights
      analytics: {},
      trendMode: 'day',
      charts: { trend: null, top: null, recurring: null },

      // by date
      quickWindow: '',
      bdStart: '',
      bdEnd: '',
      bdStage: 'ALL',
      bdRows: [],
      bdLoading: false,

      // unmatched
      unmatchedRows: [],
      unmatchedLoading: false,

      // solved
      solvedRows: [],
      solvedLoading: false,
      solvedStageFilter: 'ALL',
      markDialog: {
        open: false, stage: '', reason: '', solved_id: null,
        solved_in: 'frontend',
        solved_date: new Date().toISOString().slice(0, 10),
        fix_description: '',
      },
      reopenDialog: { open: false, reason: '', solved_id: null },

      solvedInItems: [
        { text: 'Frontend', value: 'frontend' },
        { text: 'Backend', value: 'backend' },
        { text: 'Vendor', value: 'vendor' },
        { text: 'Both (FE + BE)', value: 'both' },
      ],

      summaryHeaders: [
        { text: 'Stage', value: 'stage' },
        { text: 'Reasons', value: 'distinct_reasons', align: 'end' },
        { text: 'Solved', value: 'solved_count', align: 'end' },
        { text: 'Recurring now', value: 'regressing_count', align: 'end' },
        { text: 'Total', value: 'total_win', align: 'end' },
        { text: 'Recurring', value: 'regressions_win', align: 'end' },
        { text: 'Unmatched', value: 'unmatched_win', align: 'end' },
        { text: 'Action', value: 'action', sortable: false, align: 'end' },
      ],
      stageHeaders: [
        { text: 'Normalized Reason', value: 'reason' },
        { text: 'Status', value: 'status' },
        { text: 'Solved In', value: 'solved_in' },
        { text: 'Solved Date', value: 'solved_date' },
        { text: 'Window count', value: 'recent_count', align: 'end' },
        { text: 'Recurred (since solve)', value: 'recurred_in_window', align: 'end' },
        { text: 'All time', value: 'all_time', align: 'end' },
      ],
      byDateHeaders: [
        { text: 'Client ID', value: 'client_id' },
        { text: 'Mobile', value: 'mobile' },
        { text: 'Rejection Date', value: 'recorded_at' },
        { text: 'Stage', value: 'stage' },
        { text: 'Original Reason', value: 'raw_reason' },
        { text: 'Normalized Reason', value: 'normalized_reason' },
        { text: 'Status', value: 'status' },
      ],
      unmatchedHeaders: [
        { text: 'Original Reason', value: 'raw_reason' },
        { text: 'Stage', value: 'stage' },
        { text: 'Count', value: 'cnt', align: 'end' },
        { text: 'Suggested Canonical', value: 'suggested' },
        { text: 'Map to', value: 'action', sortable: false },
      ],
      solvedHeaders: [
        { text: 'Stage', value: 'stage' },
        { text: 'Normalized Reason', value: 'reason' },
        { text: 'Status', value: 'solved' },
        { text: 'Solved In', value: 'solved_in' },
        { text: 'Solved Date', value: 'solved_date' },
        { text: 'Fix Description', value: 'fix_description' },
        { text: 'Action', value: 'action', sortable: false, align: 'end' },
      ],

    };
  },
  computed: {
    canAccessSolved() { return this.uuid === 'ZE785' || this.uuid === 'ZE806'; },
    stageItems() { return ['ALL', ...this.stages]; },
    quickWindowItems() {
      return [{ key: '', label: 'Custom' }, ...this.windowOptions];
    },
    summaryCards() {
      const k = this.kpis || {};
      const recurTone = k.regressions_win > 0 ? 'tone-danger' : 'tone-primary';
      const unmTone = k.unmatched_win > 0 ? 'tone-warning' : 'tone-primary';
      return [
        {
          label1: 'Today', value1: k.today_rej || 0,
          label2: 'Last 7 days', value2: k.week_rej || 0,
          icon: 'mdi-calendar-blank-outline', tone: 'tone-primary', iconColor: 'primary', valueClass: '',
        },
        {
          label1: `Recurring (${this.windowLabel})`, value1: k.regressions_win || 0,
          label2: 'Last 24h', value2: k.regressions_24h || 0,
          icon: 'mdi-alert-outline', tone: recurTone,
          iconColor: recurTone === 'tone-danger' ? 'error' : 'primary',
          valueClass: recurTone === 'tone-danger' ? 'error--text' : '',
        },
        {
          label1: `Unmatched (${this.windowLabel})`, value1: k.unmatched_win || 0,
          label2: 'Needs review', value2: k.unmatched_win || 0,
          icon: 'mdi-information-outline', tone: unmTone,
          iconColor: unmTone === 'tone-warning' ? 'warning' : 'primary',
          valueClass: unmTone === 'tone-warning' ? 'warning--text' : '',
        },
        {
          label1: 'Solved reasons', value1: this.navCounts.solved || 0,
          label2: 'Stages tracked', value2: this.summaryRows.length,
          icon: 'mdi-check-circle-outline', tone: 'tone-primary', iconColor: 'primary', valueClass: '',
        },
      ];
    },
    insightCards() {
      const peakDay = this.analytics.peak_day;
      const peakMonth = this.analytics.peak_month;
      const recurTone = this.topRecurringCount > 0 ? 'tone-danger' : 'tone-primary';
      return [
        {
          label1: 'Peak day', value1: peakDay ? peakDay.count : 0,
          label2: peakDay ? this.fmtDate(peakDay.date) : '—', value2: 'rejections',
          icon: 'mdi-calendar-blank-outline', tone: 'tone-primary', iconColor: 'primary', valueClass: '',
        },
        {
          label1: 'Peak month', value1: peakMonth ? peakMonth.count : 0,
          label2: peakMonth ? this.fmtMonth(peakMonth.month) : '—', value2: 'rejections',
          icon: 'mdi-pulse', tone: 'tone-primary', iconColor: 'primary', valueClass: '',
        },
        {
          label1: 'Most common reason', value1: this.topReasonLabel,
          label2: 'hits', value2: this.topReasonCount, stacked: true,
          icon: 'mdi-clock-outline', tone: 'tone-primary', iconColor: 'primary', valueClass: '',
        },
        {
          label1: 'Worst recurring', value1: this.topRecurringLabel,
          label2: 'hits since solve', value2: this.topRecurringCount, stacked: true,
          icon: 'mdi-history', tone: recurTone,
          iconColor: recurTone === 'tone-danger' ? 'error' : 'primary',
          valueClass: recurTone === 'tone-danger' ? 'error--text' : '',
        },
      ];
    },
    byDateExportUrl() {
      return rejectionApi.byDateExportUrl({ start: this.bdStart, end: this.bdEnd, stage: this.bdStage });
    },
    mergedSolvedRows() {
      const map = {};
      this.solvedRows.forEach(r => { map[r.stage + '::' + r.normalized_reason] = r; });
      const all = [];
      Object.entries(this.stageCanonical).forEach(([stage, reasons]) => {
        reasons.forEach(reason => {
          const key = stage + '::' + reason;
          const s = map[key];
          all.push({
            stage, reason,
            solved: !!s,
            solved_id: s ? s.id : null,
            solved_in: s ? s.solved_in : null,
            solved_date: s ? s.solved_date : null,
            fix_description: s ? s.fix_description : null,
          });
        });
      });
      return all.sort((a, b) => {
        if (a.solved !== b.solved) return a.solved ? -1 : 1;
        if (a.stage !== b.stage) return a.stage.localeCompare(b.stage);
        return a.reason.localeCompare(b.reason);
      });
    },
    filteredSolvedRows() {
      if (this.solvedStageFilter === 'ALL') return this.mergedSolvedRows;
      return this.mergedSolvedRows.filter(r => r.stage === this.solvedStageFilter);
    },
    topReasonLabel() {
      const r = (this.analytics.top_reasons || [])[0];
      return r ? `${r.stage} — ${r.normalized_reason}` : '—';
    },
    topReasonCount() {
      const r = (this.analytics.top_reasons || [])[0];
      return r ? r.cnt : 0;
    },
    topRecurringLabel() {
      const r = (this.analytics.top_recurring || [])[0];
      return r ? `${r.stage} — ${r.normalized_reason}` : 'None — clean!';
    },
    topRecurringCount() {
      const r = (this.analytics.top_recurring || [])[0];
      return r ? r.cnt : 0;
    },
    hasTrendData() {
      const a = this.trendMode === 'day' ? (this.analytics.by_day || []) : (this.analytics.by_month || []);
      return a.length > 0;
    },
  },
  watch: {
    active(v) { if (v && !this.booted) this.boot(); },
    subTab(v) {
      if (!this.booted) return;
      if (v === 0) this.loadSummary();
      else if (v === 1) this.loadAnalytics();
      else if (v === 2 && !this.bdRows.length) this.loadByDate();
      else if (v === 3) this.loadUnmatched();
      else if (v === 4 && this.canAccessSolved) this.loadSolved();
    },
  },
  mounted() {
    this.uuid = localStorage.getItem('empcode');
    if (this.active) this.boot();
  },
  beforeDestroy() {
    ['trend', 'top', 'recurring'].forEach(k => this.destroyChart(k));
  },
  methods: {
    async boot() {
      this.booted = true;
      try {
        const m = await rejectionApi.meta();
        this.windowOptions = m.window_options || [];
        this.stages = m.stages || [];
        this.stageCanonical = m.stage_canonical || {};
        this.windowSel = m.default_window || '30d';
        const today = new Date().toISOString().slice(0, 10);
        const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
        this.bdStart = weekAgo;
        this.bdEnd = today;
        await Promise.all([this.loadSummary(), this.loadNavCounts()]);
      } catch (e) {
        this.notify('Failed to load rejection metadata: ' + (e.message || e), 'error');
      }
    },
    notify(msg, color = 'success') { this.$emit('notify', { msg, color }); },

    async downloadReport() {
      this.reportLoading = true;
      try {
        const days = WINDOW_TO_DAYS[this.windowSel] || 7;
        const res = await rejectionApi.weeklyReport(days);
        const cd = res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition']);
        const fallback = `rejection_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
        const filename = parseFilenameFromCD(cd) || fallback;
        downloadBlob(res.data, filename);
        this.notify('Report downloaded');
      } catch (e) {
        this.notify('Report download failed: ' + (e.message || e), 'error');
      } finally {
        this.reportLoading = false;
      }
    },

    // ----- Summary -----
    async loadSummary() {
      this.summaryLoading = true;
      try {
        const d = await rejectionApi.summary(this.windowSel);
        this.kpis = d.kpis || {};
        this.summaryRows = d.rows || [];
        this.windowLabel = d.window_label || '';
        this.bannerCount = (d.kpis && d.kpis.regressions_24h) || 0;
      } catch (e) {
        this.notify('Summary load failed: ' + (e.message || e), 'error');
      } finally {
        this.summaryLoading = false;
      }
    },
    async loadNavCounts() {
      try {
        const [u, sv] = await Promise.all([rejectionApi.unmatched(), rejectionApi.solvedList()]);
        this.navCounts = {
          recent: (this.kpis && this.kpis.week_rej) || 0,
          unmatched: (u.rows || []).length,
          solved: (sv.rows || []).filter(r => r.status === 'active').length,
        };
      } catch (e) { /* silent */ }
    },
    openStage(stage) {
      this.drillStage = stage;
      this.loadStage();
    },
    async loadStage() {
      if (!this.drillStage) return;
      this.stageLoading = true;
      try {
        const d = await rejectionApi.perStage(this.drillStage, this.windowSel);
        this.stageRows = d.rows || [];
      } catch (e) {
        this.notify('Stage load failed: ' + (e.message || e), 'error');
      } finally {
        this.stageLoading = false;
      }
    },
    statusColor(r) {
      if (r.status === 'Solved' && r.recurred_in_window > 0) return 'error';
      if (r.status === 'Solved') return 'success';
      return 'warning';
    },
    statusDotClass(r) {
      if (r.status === 'Solved' && r.recurred_in_window > 0) return 'dot-red';
      if (r.status === 'Solved') return 'dot-green';
      return 'dot-amber';
    },
    statusTextClass(r) {
      if (r.status === 'Solved' && r.recurred_in_window > 0) return 'error--text';
      if (r.status === 'Solved') return 'success--text';
      return 'warning--text';
    },
    statusLabel(r) {
      if (r.status === 'Solved' && r.recurred_in_window > 0) return 'Solved · recurring';
      return r.status;
    },

    // ----- Insights -----
    async loadAnalytics() {
      try {
        const d = await rejectionApi.analytics(this.windowSel);
        this.analytics = d;
        this.windowLabel = d.window_label || this.windowLabel;
        this.$nextTick(() => {
          this.renderTrend();
          this.renderTopReasons();
          this.renderTopRecurring();
        });
      } catch (e) {
        this.notify('Insights load failed: ' + (e.message || e), 'error');
      }
    },
    fmtDate(iso) {
      if (!iso) return '—';
      return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    fmtMonth(iso) {
      if (!iso) return '—';
      return new Date(iso).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    },
    fmtDateTime(iso) { return iso ? iso.replace('T', ' ').slice(0, 16) : ''; },
    destroyChart(key) {
      if (this.charts[key]) { this.charts[key].destroy(); this.charts[key] = null; }
    },
    renderTrend() {
      this.destroyChart('trend');
      if (!this.$refs.trendChart || !this.hasTrendData) return;
      const series = this.trendMode === 'day' ? (this.analytics.by_day || []) : (this.analytics.by_month || []);
      const labels = series.map(r => this.trendMode === 'day' ? this.fmtDate(r.date) : this.fmtMonth(r.month));
      const values = series.map(r => r.count);
      this.charts.trend = new Chart(this.$refs.trendChart, {
        type: 'line',
        data: { labels, datasets: [{
          label: 'Rejections', data: values,
          borderColor: '#0037B7', backgroundColor: 'rgba(0,55,183,0.08)',
          borderWidth: 2, fill: true, tension: 0.3, pointRadius: 3, pointBackgroundColor: '#0037B7',
        }]},
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
        },
      });
    },
    renderTopReasons() {
      this.destroyChart('top');
      const series = this.analytics.top_reasons || [];
      if (!this.$refs.topReasonsChart || !series.length) return;
      this.charts.top = new Chart(this.$refs.topReasonsChart, {
        type: 'bar',
        data: { labels: series.map(r => r.normalized_reason),
          datasets: [{ data: series.map(r => r.cnt), backgroundColor: '#0037B7', borderRadius: 4 }]},
        options: {
          indexAxis: 'y', responsive: true, maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { title: c => series[c[0].dataIndex].stage + ' · ' + series[c[0].dataIndex].normalized_reason } },
          },
          scales: { x: { beginAtZero: true, ticks: { precision: 0 } } },
        },
      });
    },
    renderTopRecurring() {
      this.destroyChart('recurring');
      const series = this.analytics.top_recurring || [];
      if (!this.$refs.topRecurringChart || !series.length) return;
      this.charts.recurring = new Chart(this.$refs.topRecurringChart, {
        type: 'bar',
        data: { labels: series.map(r => r.normalized_reason),
          datasets: [{ data: series.map(r => r.cnt), backgroundColor: '#DC2626', borderRadius: 4 }]},
        options: {
          indexAxis: 'y', responsive: true, maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { title: c => series[c[0].dataIndex].stage + ' · ' + series[c[0].dataIndex].normalized_reason } },
          },
          scales: { x: { beginAtZero: true, ticks: { precision: 0 } } },
        },
      });
    },

    // ----- By date -----
    async loadByDate() {
      this.bdLoading = true;
      try {
        const d = await rejectionApi.byDate({ start: this.bdStart, end: this.bdEnd, stage: this.bdStage });
        this.bdRows = d.rows || [];
      } catch (e) {
        this.notify('By-date load failed: ' + (e.message || e), 'error');
      } finally {
        this.bdLoading = false;
      }
    },
    async onQuickWindow() {
      if (!this.quickWindow) return;
      this.bdLoading = true;
      try {
        const d = await rejectionApi.byDate({ stage: this.bdStage, window: this.quickWindow });
        this.bdStart = d.start; this.bdEnd = d.end; this.bdRows = d.rows || [];
      } finally { this.bdLoading = false; }
    },
    byDateStatusColor(s) {
      if (s === 'Recurring') return 'error';
      if (s === 'Unmatched') return 'warning';
      return 'grey';
    },
    byDateDotClass(s) {
      if (s === 'Recurring') return 'dot-red';
      if (s === 'Unmatched') return 'dot-amber';
      return 'dot-gray';
    },
    byDateTextClass(s) {
      if (s === 'Recurring') return 'error--text';
      if (s === 'Unmatched') return 'warning--text';
      return 'grey--text text--darken-1';
    },

    // ----- Unmatched -----
    async loadUnmatched() {
      this.unmatchedLoading = true;
      try {
        const d = await rejectionApi.unmatched();
        this.unmatchedRows = (d.rows || []).map(r => ({ ...r, _choice: r.suggested || (r.canonical_options && r.canonical_options[0]) || '' }));
      } catch (e) {
        this.notify('Unmatched load failed: ' + (e.message || e), 'error');
      } finally { this.unmatchedLoading = false; }
    },
    async mapRow(r) {
      try {
        await rejectionApi.mapUnmatched({ stage: r.stage, raw_reason: r.raw_reason, canonical_reason: r._choice });
        this.notify('Mapped successfully');
        await this.loadUnmatched();
        await this.loadNavCounts();
      } catch (e) {
        this.notify('Map failed: ' + (e.message || e), 'error');
      }
    },

    // ----- Solved -----
    async loadSolved() {
      this.solvedLoading = true;
      try {
        const d = await rejectionApi.solvedList();
        this.solvedRows = d.rows || [];
      } catch (e) {
        this.notify('Solved load failed: ' + (e.message || e), 'error');
      } finally { this.solvedLoading = false; }
    },
    openMarkSolved(r) {
      this.markDialog = {
        open: true, stage: r.stage, reason: r.reason, solved_id: null,
        solved_in: 'frontend',
        solved_date: new Date().toISOString().slice(0, 10),
        fix_description: '',
      };
    },
    async submitMarkSolved() {
      try {
        await rejectionApi.solvedAdd({
          stage: this.markDialog.stage,
          normalized_reason: this.markDialog.reason,
          solved_in: this.markDialog.solved_in,
          solved_date: this.markDialog.solved_date,
          historical_count: 0,
          fix_description: this.markDialog.fix_description || '',
        });
        this.markDialog.open = false;
        await this.loadSolved();
        await this.loadNavCounts();
        this.notify('Marked as solved');
      } catch (e) {
        this.notify('Mark solved failed: ' + (e.message || e), 'error');
      }
    },
    askReopen(r) {
      this.reopenDialog = { open: true, reason: r.reason, solved_id: r.solved_id };
    },
    async confirmReopen() {
      try {
        await rejectionApi.solvedDelete(this.reopenDialog.solved_id);
        this.reopenDialog.open = false;
        await this.loadSolved();
        await this.loadNavCounts();
        this.notify('Reopened');
      } catch (e) {
        this.notify('Reopen failed: ' + (e.message || e), 'error');
      }
    },
    hasFE(r) { return r.solved_in === 'frontend' || r.solved_in === 'both'; },
    hasBE(r) { return r.solved_in === 'backend' || r.solved_in === 'both'; },
    hasVendor(r) { return r.solved_in === 'vendor'; },
  },
};
</script>

<style scoped>
.text-no-wrap { white-space: nowrap; }

.status-cell { display: inline-flex; align-items: center; gap: 8px; }
.status-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.dot-green { background: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,.15); }
.dot-amber { background: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,.15); }
.dot-red { background: #dc2626; box-shadow: 0 0 0 3px rgba(220,38,38,.18); }
.dot-gray { background: #94a3b8; }

.pill {
  display: inline-block;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  border-radius: 6px;
  border: 1px solid transparent;
  line-height: 1.4;
}
.pill-fe { background: #eef0fe; color: #1a37d6; border-color: #c7cffb; }
.pill-be { background: #dcfce7; color: #15803d; border-color: #bbf7d0; }
.pill-vendor { background: #fef3c7; color: #b45309; border-color: #fcd34d; }

.count-badge.v-chip.v-size--x-small {
  height: 20px;
  min-width: 20px;
  padding: 0 6px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 11px;
  justify-content: center;
}
.count-badge.v-chip.v-size--x-small >>> .v-chip__content {
  padding: 0;
}

.stat-card {
  position: relative;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
  padding: 16px 20px 16px 22px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15,23,42,.03);
  transition: transform .15s, box-shadow .15s;
  height: 100%;
}
.stat-card:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(15,23,42,.06); }
.stat-card::before {
  content: '';
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 4px;
  background: #0037B7;
}
.stat-card.tone-danger::before { background: #DC2626; }
.stat-card.tone-warning::before { background: #F59E0B; }

.stat-icon {
  width: 36px; height: 36px;
  border-radius: 10px;
  background: #EEF0FE;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}
.stat-icon.tone-danger { background: #FEE2E2; }
.stat-icon.tone-warning { background: #FEF3C7; }

.stat-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.stat-line-2 {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #F1F3F7;
}
.stat-label {
  color: #6B7280;
  font-size: 13px;
  font-weight: 500;
}
.stat-value {
  color: #0F172A;
  font-weight: 700;
  font-size: 22px;
  letter-spacing: -0.02em;
}
.stat-value-sm {
  font-size: 14px;
  color: #6B7280;
  font-weight: 600;
}
::v-deep .compact-select .v-input__control > .v-input__slot {
  min-height: 40px !important;
}
::v-deep .compact-select .v-select__selection--comma {
  font-size: 13px;
}
::v-deep .compact-select .v-label {
  font-size: 13px;
}
::v-deep .compact-select .v-input__append-inner {
  margin-top: 8px !important;
}
.compact-select { margin-top: 6px; }

::v-deep .map-select .v-input__control > .v-input__slot {
  min-height: 30px !important;
  padding: 0 8px !important;
}
::v-deep .map-select .v-select__selection--comma {
  font-size: 12.5px;
  margin: 0 !important;
}
::v-deep .map-select .v-input__append-inner {
  margin-top: 3px !important;
}
::v-deep .map-select fieldset { padding-left: 6px; }
::v-deep .map-select { margin: 6px 0; }


.action-btn.v-btn {
  height: 26px !important;
  font-size: 11.5px !important;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 0 10px !important;
}

.stat-value-text {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  display: block;
  margin-top: 2px;
  color: #334155;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  font-size: 13px !important;
  height: 44px;
  color: #6B7280;
  font-weight: 600;
}
::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  font-size: 13.5px !important;
  height: 44px;
}
</style>
